import React, { useState, useEffect, useRef, memo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locals/translations.json";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { gsap } from "gsap";
import { generateAuctionLanePDF } from "../services/pdfService.ts";

//import "./counter.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

const SingleAuction = memo(
  ({ auction, isSelected = false, onSelect, index = 0 }) => {
    console.log("Single auction", {
      auctionId: auction?.id,
      auctionName: auction?.auction || auction?.name,
      auctionDescription: auction?.description,

      lanes: auction?.auctionLanes?.map((lane) => ({
        id: lane.id,
        name: lane.name,
        status: lane.status,
        vehicleCount:
          lane.vehicles?.length || lane.auctionLaneVehicles?.length || 0,
      })),
    });

    const navigate = useNavigate();
    const { language } = useLanguage();
    const [isHovered, setIsHovered] = useState(false);

    // Countdown timer refs
    const daysRef = useRef(null);
    const hoursRef = useRef(null);
    const minutesRef = useRef(null);
    const secondsRef = useRef(null);

    // Initial countdown values (only used for initial render)
    const getInitialTimeLeft = () => {
      if (!auction?.startDate) return { d: 0, h: 0, m: 0, s: 0 };

      const targetDateTime = new Date(auction.startDate);
      if (isNaN(targetDateTime.getTime())) return { d: 0, h: 0, m: 0, s: 0 };

      const now = new Date();
      const totalSeconds = Math.floor((targetDateTime - now) / 1000);

      if (totalSeconds < 0) return { d: 0, h: 0, m: 0, s: 0 };

      return {
        d: Math.floor(totalSeconds / (60 * 60 * 24)),
        h: Math.floor((totalSeconds / (60 * 60)) % 24),
        m: Math.floor((totalSeconds / 60) % 60),
        s: Math.floor(totalSeconds % 60),
      };
    };

    const [timeLeft] = useState(getInitialTimeLeft);

    // Check if auction is active (not closed)
    // First check if there's a status property from API, otherwise calculate based on time
    // Don't mark as closed until ALL lanes have "completed" status
    const areAllLanesCompleted = auction?.auctionLanes?.every((lane) => {
      const laneStatus =
        typeof lane.status === "string"
          ? lane.status
          : lane.status?.name || lane.status?.slug;
      return laneStatus === "completed";
    });

    // Check if any lane has "Ongoing" or "Paused" status
    const hasActiveLanes = auction?.auctionLanes?.some((lane) => {
      const laneStatus =
        typeof lane.status === "string"
          ? lane.status
          : lane.status?.name || lane.status?.slug;
      return (
        laneStatus === "ongoing" ||
        laneStatus === "Ongoing" ||
        laneStatus === "paused" ||
        laneStatus === "Paused"
      );
    });

    const isActive =
      auction &&
      (auction.status === "active" ||
        auction.status === "live" ||
        hasActiveLanes || // Make live if any lane is ongoing or paused
        (auction.status !== "closed" &&
          !areAllLanesCompleted &&
          dayjs
            .utc(auction.startDate)
            .tz(DUBAI_TZ)
            .isAfter(dayjs().tz(DUBAI_TZ))));

    // Check if auction should be marked as "live" based on lane status
    const isLive = hasActiveLanes || auction?.status === "live";

    // Format auction date
    const formatAuctionDate = (date) => {
      const auctionDate = dayjs.utc(date).tz(DUBAI_TZ);
      if (language === "ar") {
        return `${
          translations[language]?.days?.[
            auctionDate.format("dddd").toLowerCase()
          ] || auctionDate.format("dddd")
        }, ${
          translations[language]?.months?.[
            auctionDate.format("MMMM").toLowerCase()
          ] || auctionDate.format("MMMM")
        } ${
          translations[language]?.numbers?.[auctionDate.format("D")] ||
          auctionDate.format("D")
        }`;
      }
      return auctionDate.format("dddd, D MMMM YYYY");
    };

    // Format auction time
    const formatAuctionTime = (date) => {
      return dayjs.utc(date).tz(DUBAI_TZ).format("hh:mm A");
    };

    // Handle auction selection
    const handleAuctionSelect = () => {
      if (onSelect) {
        onSelect(auction);
      }
    };

    // Handle lane navigation
    const handleViewList = (laneId) => {
      navigate(`/vehicles?auctionId=${auction.id}&laneId=${laneId}`);
    };

    const handleJoinAuction = (laneId) => {
      navigate(`/livebid/${auction.id}/${laneId}`);
    };

    // Handle PDF download
    const handleDownloadPDF = async (laneId, laneName) => {
      try {
        const auctionName = auction.auction || auction.name || "Auction";
        const auctionDate = auction.startDate; // Use the auction start date
        await generateAuctionLanePDF(
          auction.id,
          laneId,
          auctionName,
          laneName,
          auctionDate
        );
      } catch (error) {
        console.error("Error downloading PDF:", error);
        alert("Failed to download PDF. Please try again.");
      }
    };

    // Countdown timer logic - updates DOM directly without causing re-renders
    useEffect(() => {
      if (!auction?.startDate) return;

      const targetDateTime = new Date(auction.startDate);
      if (isNaN(targetDateTime.getTime())) return;

      const updateTime = () => {
        const now = new Date();
        const totalSeconds = Math.floor((targetDateTime - now) / 1000);

        if (totalSeconds < 0) return;

        const d = Math.floor(totalSeconds / (60 * 60 * 24));
        const h = Math.floor((totalSeconds / (60 * 60)) % 24);
        const m = Math.floor((totalSeconds / 60) % 60);
        const s = Math.floor(totalSeconds % 60);

        // Update DOM directly without triggering re-renders
        updateCountdownDisplay(d, h, m, s);

        // Animate flip for each time unit
        if (daysRef.current) animateFlip(daysRef.current, d);
        if (hoursRef.current) animateFlip(hoursRef.current, h);
        if (minutesRef.current) animateFlip(minutesRef.current, m);
        if (secondsRef.current) animateFlip(secondsRef.current, s);
      };

      const interval = setInterval(updateTime, 1000);
      updateTime(); // Initial call

      return () => clearInterval(interval);
    }, [auction?.startDate]);

    // Function to update countdown display directly in DOM without state updates
    const updateCountdownDisplay = (d, h, m, s) => {
      const formatValue = (value) => (value < 10 ? `0${value}` : `${value}`);

      // Update days
      if (daysRef.current) {
        const topElement = daysRef.current.querySelector(".top");
        const bottomElement = daysRef.current.querySelector(".bottom");
        const topBackElement = daysRef.current.querySelector(".top-back span");
        const bottomBackElement =
          daysRef.current.querySelector(".bottom-back span");

        if (topElement) topElement.textContent = formatValue(d);
        if (bottomElement) bottomElement.textContent = formatValue(d);
        if (topBackElement) topBackElement.textContent = formatValue(d);
        if (bottomBackElement) bottomBackElement.textContent = formatValue(d);
      }

      // Update hours
      if (hoursRef.current) {
        const topElement = hoursRef.current.querySelector(".top");
        const bottomElement = hoursRef.current.querySelector(".bottom");
        const topBackElement = hoursRef.current.querySelector(".top-back span");
        const bottomBackElement =
          hoursRef.current.querySelector(".bottom-back span");

        if (topElement) topElement.textContent = formatValue(h);
        if (bottomElement) bottomElement.textContent = formatValue(h);
        if (topBackElement) topBackElement.textContent = formatValue(h);
        if (bottomBackElement) bottomBackElement.textContent = formatValue(h);
      }

      // Update minutes
      if (minutesRef.current) {
        const topElement = minutesRef.current.querySelector(".top");
        const bottomElement = minutesRef.current.querySelector(".bottom");
        const topBackElement =
          minutesRef.current.querySelector(".top-back span");
        const bottomBackElement =
          minutesRef.current.querySelector(".bottom-back span");

        if (topElement) topElement.textContent = formatValue(m);
        if (bottomElement) bottomElement.textContent = formatValue(m);
        if (topBackElement) topBackElement.textContent = formatValue(m);
        if (bottomBackElement) bottomBackElement.textContent = formatValue(m);
      }

      // Update seconds
      if (secondsRef.current) {
        const topElement = secondsRef.current.querySelector(".top");
        const bottomElement = secondsRef.current.querySelector(".bottom");
        const topBackElement =
          secondsRef.current.querySelector(".top-back span");
        const bottomBackElement =
          secondsRef.current.querySelector(".bottom-back span");

        if (topElement) topElement.textContent = formatValue(s);
        if (bottomElement) bottomElement.textContent = formatValue(s);
        if (topBackElement) topBackElement.textContent = formatValue(s);
        if (bottomBackElement) bottomBackElement.textContent = formatValue(s);
      }
    };

    // GSAP flip animation function
    const animateFlip = (element, value) => {
      const bottomBack = element.querySelector(".bottom-back span");
      const topBack = element.querySelector(".top-back span");
      const top = element.querySelector(".top");
      const bottom = element.querySelector(".bottom");

      const currentValue = value < 10 ? `0${value}` : `${value}`;
      if (bottomBack?.innerText === currentValue) return;

      if (topBack) topBack.innerText = currentValue;
      if (bottomBack) bottomBack.innerText = currentValue;

      gsap.to(top, {
        duration: 0.7,
        rotationX: -180,
        transformPerspective: 300,
        ease: "quart.out",
        onComplete: () => {
          if (top) top.innerText = currentValue;
          if (bottom) bottom.innerText = currentValue;
          gsap.set(top, { rotationX: 0 });
        },
      });

      gsap.to(element.querySelector(".top-back"), {
        duration: 0.7,
        rotationX: 0,
        transformPerspective: 300,
        ease: "quart.out",
        clearProps: "all",
      });
    };

    if (!auction || typeof auction !== "object") {
      return (
        <div className='bg-gray-800 p-6 rounded-lg border border-gray-700'>
          <div className='text-center text-gray-400'>
            <p>No auction data available</p>
            <p className='text-sm'>
              Debug: auction = {JSON.stringify(auction)}
            </p>
          </div>
        </div>
      );
    }

    // Ensure required properties exist and are valid
    if (!auction.startDate) {
      console.warn("Auction object missing startDate:", auction);
      return null;
    }

    // Check for auction name in different possible properties
    const auctionName = auction.auction || auction.name || "Auction";
    if (!auctionName || typeof auctionName !== "string") {
      console.warn("Auction object missing or invalid auction name:", auction);
      return null;
    }

    return (
      <motion.div
        className={`relative overflow-hidden transition-all duration-300 min-w-[300px] bg-gray-800`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleAuctionSelect}
        whileHover={{ y: -5 }}
        transition={{ duration: 0.3 }}
      >
        {/* Background with gradient overlay */}
        <div className='absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] opacity-90' />

        {/* Animated background pattern */}
        <div className='absolute inset-0 opacity-5'>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23F15A29%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20" />
        </div>

        {/* Content */}
        <div className='relative z-10 p-3'>
          {/* Header Section */}
          <div className='mb-6'>
            <div className='flex justify-between items-start mb-4'>
              <div className='flex-1'>
                <h2 className='text-white text-[20px] font-bold mb-2 capitalize'>
                  {auctionName}
                </h2>
                <div className='text-gray-400 font-sm'>
                  {formatAuctionDate(auction.startDate)} {" | "}
                  {formatAuctionTime(auction.startDate)}
                </div>
              </div>

              <div className='text-right'>
                {/* <div className="bg-[#F15A29] text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                {formatAuctionTime(auction.startDate)}
              </div> */}
                <div className='text-gray-400'>
                  <span
                    className={`font-semibold text-sm rounded-full px-2 py-1 ml-1 ${
                      isActive
                        ? areAllLanesCompleted
                          ? "bg-purple-500 text-white"
                          : isLive
                          ? "bg-green-500 text-white"
                          : "bg-gray-500 text-white"
                        : auction?.status === "upcoming"
                        ? "bg-gray-500 text-white"
                        : "bg-gray-500 text-white"
                    }`}
                  >
                    {isActive
                      ? areAllLanesCompleted
                        ? translations[language]?.completed || "Completed"
                        : isLive
                        ? translations[language]?.live || "Live"
                        : translations[language]?.scheduled || "Scheduled"
                      : auction?.status === "upcoming"
                      ? translations[language]?.scheduled || "Scheduled"
                      : translations[language]?.closed || "Closed"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Countdown Timer Section */}
          <div className='mb-6'>
            <hr className='border-t-2 border-[#F15A29] w-[220px] mb-3' />

            <div className=' mb-[15px] flex items-center gap-x-2'>
              <svg
                width='27'
                height='27'
                viewBox='0 0 33 34'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M30.0076 16.9935C30.0076 24.5146 23.9106 30.6116 16.3895 30.6116C8.8685 30.6116 2.77148 24.5146 2.77148 16.9935C2.77148 9.4725 8.8685 3.37549 16.3895 3.37549C23.9106 3.37549 30.0076 9.4725 30.0076 16.9935Z'
                  fill='white'
                />
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M16.3895 10.525C16.9536 10.525 17.4109 10.9822 17.4109 11.5463V16.5705L20.5163 19.6759C20.9151 20.0747 20.9151 20.7214 20.5163 21.1203C20.1174 21.5191 19.4707 21.5191 19.0719 21.1203L15.6673 17.7158C15.4758 17.5242 15.3682 17.2644 15.3682 16.9935V11.5463C15.3682 10.9822 15.8255 10.525 16.3895 10.525Z'
                  fill='#F15A29'
                />
                <path
                  d='M30.0076 16.9935C30.0076 24.5146 23.9106 30.6116 16.3895 30.6116C8.8685 30.6116 2.77148 24.5146 2.77148 16.9935C2.77148 9.4725 8.8685 3.37549 16.3895 3.37549C23.9106 3.37549 30.0076 9.4725 30.0076 16.9935Z'
                  stroke='#F15A29'
                  stroke-width='1.02135'
                  stroke-linecap='round'
                  strokeLinejoin='round'
                />
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M16.3895 10.525C16.9536 10.525 17.4109 10.9822 17.4109 11.5463V16.5705L20.5163 19.6759C20.9151 20.0747 20.9151 20.7214 20.5163 21.1203C20.1174 21.5191 19.4707 21.5191 19.0719 21.1203L15.6673 17.7158C15.4758 17.5242 15.3682 17.2644 15.3682 16.9935V11.5463C15.3682 10.9822 15.8255 10.525 16.3895 10.525Z'
                  stroke='#F15A29'
                  stroke-width='1.02135'
                  stroke-linecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <h1 className='text-[20px] font-[500]'>
                <span className=' text-[#DE542E] font-[400]'>
                  {translations[language].liveAuctions.auction}{" "}
                </span>
                <span className='text-white'>
                  {translations[language].counter.startIn}
                </span>
              </h1>
            </div>
            {isActive ? (
              <div>
                {/* Countdown Timer Display */}
                {areAllLanesCompleted ? (
                  <div className='text-center py-8'>
                    <div className='bg-gradient-to-r from-purple-600 via-purple-500 to-purple-600 rounded-lg p-6 text-center border border-purple-400'>
                      <h1 className='text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-white text-xl font-extrabold tracking-widest'>
                        ✅{" "}
                        {translations[language]?.auctionCompleted ||
                          "Auction Completed"}
                      </h1>
                      <p className='text-purple-100 text-sm mt-2'>
                        {translations[language]?.allLanesCompleted ||
                          "All lanes have been completed"}
                      </p>
                    </div>
                  </div>
                ) : isLive &&
                  timeLeft.d === 0 &&
                  timeLeft.h === 0 &&
                  timeLeft.m === 0 &&
                  timeLeft.s === 0 ? null : (
                  <div className='text-center containDigits'>
                    <div className='content'>
                      <div
                        className='w-full bg-transparent flex flex-wrap justify-center items-center'
                        dir='ltr'
                      >
                        {/* Days */}
                        <div className='flex flex-col items-center mr-[10px]'>
                          <span
                            className='days time-elem text-[2em] max-[480px]:text-[1.5em] max-[400px]:text-[1em]'
                            ref={daysRef}
                          >
                            <span className='top text-[48px] sm:text-[42px]'>
                              {timeLeft.d < 10 ? `0${timeLeft.d}` : timeLeft.d}
                            </span>
                            <span className='top-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.d < 10
                                  ? `0${timeLeft.d}`
                                  : timeLeft.d}
                              </span>
                            </span>
                            <span className='bottom text-[48px] sm:text-[42px]'>
                              {timeLeft.d < 10 ? `0${timeLeft.d}` : timeLeft.d}
                            </span>
                            <span className='bottom-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.d < 10
                                  ? `0${timeLeft.d}`
                                  : timeLeft.d}
                              </span>
                            </span>
                          </span>
                          <span className='text-[#ffffff] text-[10px] uppercase font-light'>
                            {translations[language]?.common?.time?.days ||
                              "Days"}
                          </span>
                        </div>

                        {/* Hours */}
                        <div className='flex flex-col items-center mr-[10px]'>
                          <span
                            className='hours time-elem text-[2em] max-[480px]:text-[1.5em] max-[400px]:text-[1em]'
                            ref={hoursRef}
                          >
                            <span className='top text-[48px] sm:text-[42px]'>
                              {timeLeft.h < 10 ? `0${timeLeft.h}` : timeLeft.h}
                            </span>
                            <span className='top-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.h < 10
                                  ? `0${timeLeft.h}`
                                  : timeLeft.h}
                              </span>
                            </span>
                            <span className='bottom text-[48px] sm:text-[42px]'>
                              {timeLeft.h < 10 ? `0${timeLeft.h}` : timeLeft.h}
                            </span>
                            <span className='bottom-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.h < 10
                                  ? `0${timeLeft.h}`
                                  : timeLeft.h}
                              </span>
                            </span>
                          </span>
                          <span className='text-[#ffffff] text-[10px] uppercase font-light'>
                            {translations[language]?.common?.time?.hours ||
                              "Hours"}
                          </span>
                        </div>

                        {/* Minutes */}
                        <div className='flex flex-col items-center mr-[10px]'>
                          <span
                            className='minutes time-elem text-[2em] max-[480px]:text-[1.5em] max-[400px]:text-[1em]'
                            ref={minutesRef}
                          >
                            <span className='top text-[48px] sm:text-[42px]'>
                              {timeLeft.m < 10 ? `0${timeLeft.m}` : timeLeft.m}
                            </span>
                            <span className='top-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.m < 10
                                  ? `0${timeLeft.m}`
                                  : timeLeft.m}
                              </span>
                            </span>
                            <span className='bottom text-[48px] sm:text-[42px]'>
                              {timeLeft.m < 10 ? `0${timeLeft.m}` : timeLeft.m}
                            </span>
                            <span className='bottom-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.m < 10
                                  ? `0${timeLeft.m}`
                                  : timeLeft.m}
                              </span>
                            </span>
                          </span>
                          <span className='text-[#ffffff] text-[10px] uppercase font-light'>
                            {translations[language]?.common?.time?.minutes ||
                              "Minutes"}
                          </span>
                        </div>

                        {/* Seconds */}
                        <div className='flex flex-col items-center mr-[10px]'>
                          <span
                            className='seconds time-elem text-[2em] max-[480px]:text-[1.5em] max-[400px]:text-[1em]'
                            ref={secondsRef}
                          >
                            <span className='top text-[48px] sm:text-[42px]'>
                              {timeLeft.s < 10 ? `0${timeLeft.s}` : timeLeft.s}
                            </span>
                            <span className='top-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.s < 10
                                  ? `0${timeLeft.s}`
                                  : timeLeft.s}
                              </span>
                            </span>
                            <span className='bottom text-[48px] sm:text-[42px]'>
                              {timeLeft.s < 10 ? `0${timeLeft.s}` : timeLeft.s}
                            </span>
                            <span className='bottom-back'>
                              <span className='text-[48px] sm:text-[42px]'>
                                {timeLeft.s < 10
                                  ? `0${timeLeft.s}`
                                  : timeLeft.s}
                              </span>
                            </span>
                          </span>
                          <span className='text-[#ffffff] text-[10px] uppercase font-light'>
                            {translations[language]?.common?.time?.seconds ||
                              "Seconds"}
                          </span>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className='flex flex-col justify-evenly h-full gap-2 mt-5 mb-5 ml-2'>
                        <div className='font-[300] flex items-center gap-2'>
                          <svg
                            width='25'
                            height='25'
                            viewBox='0 0 25 25'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M12.3053 22.878C17.9461 22.878 22.5189 20.8203 22.5189 18.2819C22.5189 16.9882 21.3311 15.8194 19.4195 14.9841C18.253 17.1247 16.4742 18.9698 14.2148 19.9354C13.0003 20.4544 11.6104 20.4544 10.3959 19.9354C8.13645 18.9698 6.35765 17.1247 5.19118 14.9841C3.27958 15.8194 2.0918 16.9882 2.0918 18.2819C2.0918 20.8203 6.66456 22.878 12.3053 22.878Z'
                              fill='#F15A29'
                            />
                            <path
                              fillRule='evenodd'
                              clipRule='evenodd'
                              d='M5.15625 9.10443C5.15625 5.42967 8.35718 2.45068 12.3057 2.45068C16.2543 2.45068 19.4552 5.42967 19.4552 9.10443C19.4552 12.7504 17.1733 17.0049 13.6131 18.5263C12.7832 18.881 11.8283 18.881 10.9983 18.5263C7.43812 17.0049 5.15625 12.7504 5.15625 9.10443ZM12.3057 11.6429C13.4339 11.6429 14.3484 10.7283 14.3484 9.60016C14.3484 8.47201 13.4339 7.55746 12.3057 7.55746C11.1776 7.55746 10.263 8.47201 10.263 9.60016C10.263 10.7283 11.1776 11.6429 12.3057 11.6429Z'
                              fill='#F15A29'
                            />
                          </svg>
                          <p className='text-[14px] text-white max-[490px]:text-[10px]'>
                            {translations[language]?.counter?.location ||
                              "Khat Al Jazeera Cars Auction, Al Sajaa"}
                          </p>
                        </div>
                        <div className='font-[300] flex items-center gap-2'>
                          <svg
                            width='20'
                            height='20'
                            viewBox='0 0 18 18'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M7.82324 6.219C8.65882 4.72005 9.07661 3.97058 9.70123 3.97058C10.3258 3.97058 10.7436 4.72005 11.5792 6.21899L11.7954 6.60679C12.0328 7.03274 12.1515 7.24572 12.3367 7.38624C12.5218 7.52677 12.7523 7.57893 13.2134 7.68325L13.6332 7.77823C15.2558 8.14536 16.0671 8.32892 16.2601 8.94962C16.4531 9.57031 15.9 10.2171 14.7938 11.5106L14.5077 11.8452C14.1933 12.2128 14.0362 12.3966 13.9654 12.624C13.8947 12.8514 13.9185 13.0966 13.966 13.587L14.0093 14.0335C14.1765 15.7593 14.2601 16.6223 13.7548 17.0059C13.2495 17.3895 12.4899 17.0397 10.9707 16.3402L10.5776 16.1593C10.1459 15.9605 9.93004 15.8611 9.70123 15.8611C9.47242 15.8611 9.25656 15.9605 8.82484 16.1593L8.4318 16.3402C6.91257 17.0397 6.15296 17.3895 5.64763 17.0059C5.1423 16.6223 5.22592 15.7593 5.39316 14.0335L5.43643 13.587C5.48395 13.0966 5.50771 12.8514 5.43701 12.624C5.3663 12.3966 5.20913 12.2128 4.89479 11.8452L4.60861 11.5106C3.50244 10.2171 2.94935 9.57031 3.14237 8.94962C3.33539 8.32892 4.14668 8.14536 5.76927 7.77823L6.18905 7.68325C6.65014 7.57893 6.88068 7.52677 7.06579 7.38624C7.2509 7.24572 7.36963 7.03274 7.60707 6.60679L7.82324 6.219Z'
                              fill='#E95923'
                            />
                            <path
                              d='M3.7777 2.03071C3.80097 1.94861 3.94669 1.94812 3.97052 2.03006C4.07933 2.40422 4.28109 2.95727 4.58619 3.26032C4.89129 3.56336 5.44569 3.76138 5.82058 3.86766C5.90268 3.89094 5.90317 4.03666 5.82123 4.06049C5.44707 4.1693 4.89402 4.37106 4.59098 4.67616C4.28793 4.98126 4.08991 5.53566 3.98363 5.91055C3.96036 5.99264 3.81463 5.99314 3.79081 5.9112C3.682 5.53704 3.48023 4.98399 3.17513 4.68094C2.87003 4.3779 2.31563 4.17988 1.94074 4.0736C1.85865 4.05032 1.85816 3.9046 1.94009 3.88077C2.31425 3.77196 2.86731 3.5702 3.17035 3.2651C3.4734 2.96 3.67142 2.4056 3.7777 2.03071Z'
                              fill='#E95923'
                            />
                            <path
                              fillRule='evenodd'
                              clipRule='evenodd'
                              d='M14.7458 2.61255C15.0673 2.61255 15.3279 2.87315 15.3279 3.19461V3.38864H15.5219C15.8434 3.38864 16.104 3.64923 16.104 3.9707C16.104 4.29217 15.8434 4.55276 15.5219 4.55276H15.3279V4.74679C15.3279 5.06825 15.0673 5.32885 14.7458 5.32885C14.4244 5.32885 14.1638 5.06825 14.1638 4.74679V4.55276H13.9698C13.6483 4.55276 13.3877 4.29217 13.3877 3.9707C13.3877 3.64923 13.6483 3.38864 13.9698 3.38864H14.1638V3.19461C14.1638 2.87315 14.4244 2.61255 14.7458 2.61255Z'
                              fill='#E95923'
                            />
                          </svg>
                          <p className='text-[14px] text-white max-[490px]:text-[10px]'>
                            {auction?.description ||
                              translations[language]?.counter
                                ?.majorityGreenLight ||
                              "Majority green light vehicles"}
                          </p>
                        </div>
                        <div className='font-[300] flex items-center gap-2'>
                          <svg
                            width='24'
                            height='19'
                            viewBox='0 0 24 19'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M23.0289 7.51646H21.9948L19.3329 1.53196C19.2136 1.26077 19.0179 1.03025 18.7695 0.868677C18.5212 0.707101 18.2312 0.621484 17.9349 0.622321H6.67447C6.37822 0.621484 6.08816 0.707101 5.83984 0.868677C5.59152 1.03025 5.39574 1.26077 5.27649 1.53196L2.61459 7.51646H1.58047C1.37731 7.51646 1.18247 7.59717 1.03881 7.74082C0.895158 7.88448 0.814453 8.07932 0.814453 8.28248C0.814453 8.48564 0.895158 8.68048 1.03881 8.82413C1.18247 8.96779 1.37731 9.04849 1.58047 9.04849H2.34648V16.7087C2.34648 17.115 2.50789 17.5046 2.79521 17.792C3.08252 18.0793 3.4722 18.2407 3.87852 18.2407H6.17656C6.58288 18.2407 6.97256 18.0793 7.25987 17.792C7.54718 17.5046 7.7086 17.115 7.7086 16.7087V15.1766H16.9008V16.7087C16.9008 17.115 17.0622 17.5046 17.3495 17.792C17.6368 18.0793 18.0265 18.2407 18.4328 18.2407H20.7309C21.1372 18.2407 21.5269 18.0793 21.8142 17.792C22.1015 17.5046 22.2629 17.115 22.2629 16.7087V9.04849H23.0289C23.2321 9.04849 23.4269 8.96779 23.5706 8.82413C23.7142 8.68048 23.7949 8.48564 23.7949 8.28248C23.7949 8.07932 23.7142 7.88448 23.5706 7.74082C23.4269 7.59717 23.2321 7.51646 23.0289 7.51646ZM7.7086 12.1126H6.17656C5.9734 12.1126 5.77856 12.0319 5.63491 11.8882C5.49125 11.7445 5.41055 11.5497 5.41055 11.3465C5.41055 11.1434 5.49125 10.9485 5.63491 10.8049C5.77856 10.6612 5.9734 10.5805 6.17656 10.5805H7.7086C7.91176 10.5805 8.10659 10.6612 8.25025 10.8049C8.39391 10.9485 8.47461 11.1434 8.47461 11.3465C8.47461 11.5497 8.39391 11.7445 8.25025 11.8882C8.10659 12.0319 7.91176 12.1126 7.7086 12.1126ZM16.9008 12.1126C16.6976 12.1126 16.5028 12.0319 16.3591 11.8882C16.2155 11.7445 16.1348 11.5497 16.1348 11.3465C16.1348 11.1434 16.2155 10.9485 16.3591 10.8049C16.5028 10.6612 16.6976 10.5805 16.9008 10.5805H18.4328C18.636 10.5805 18.8308 10.6612 18.9745 10.8049C19.1181 10.9485 19.1988 11.1434 19.1988 11.3465C19.1988 11.5497 18.1181 11.7445 18.9745 11.8882C18.8308 12.0319 18.636 12.1126 18.4328 12.1126H16.9008Z'
                              fill='#F15A29'
                            />
                          </svg>
                          <p className='text-white text-[14px] font-[300] max-[490px]:text-[10px]'>
                            {typeof auction.totalCars === "number"
                              ? auction.totalCars
                              : Array.isArray(auction.auctionLanes)
                              ? auction.auctionLanes.reduce(
                                  (sum, lane) =>
                                    sum +
                                    (Array.isArray(lane.auctionLaneVehicles)
                                      ? lane.auctionLaneVehicles.length
                                      : 0),
                                  0
                                )
                              : 0}{" "}
                            {translations[language]?.counter?.carsInAuction ||
                              "Cars in Auction"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='bg-gradient-to-r from-[#161616] via-[#505050] to-[#161616] rounded-lg p-6 text-center border border-gray-700'>
                <h1 className='text-transparent bg-clip-text bg-gradient-to-r from-[#ff5100] via-[#ff8800] to-[#ffe600] text-xl font-extrabold tracking-widest animate-pulse'>
                  ⌛ {translations[language]?.auctionClosed || "Auction Closed"}
                </h1>
              </div>
            )}
          </div>

          {/* Auction Lanes Section */}
          <div className='space-y-3'>
            <div className='grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3'>
              {auction.auctionLanes
                ?.filter((lane) => lane && typeof lane === "object")
                .map((lane, idx) => (
                  <motion.div
                    key={lane.id || idx}
                    className={`bg-black/50 backdrop-blur-sm rounded-sm p-2 border border-gray-700 hover:border-[#F15A29] transition-all duration-300 relative ${(() => {
                      const laneStatus =
                        typeof lane.status === "string"
                          ? lane.status
                          : lane.status?.name || lane.status?.slug;

                      // if (
                      //   laneStatus === "completed" ||
                      //   laneStatus === "Completed"
                      // ) {
                      //   return "bg-blue-500";
                      // } else if (
                      //   laneStatus === "ongoing" ||
                      //   laneStatus === "Ongoing"
                      // ) {
                      //   return "bg-green-950";
                      // } else if (
                      //   laneStatus === "paused" ||
                      //   laneStatus === "Paused"
                      // ) {
                      //   return "bg-yellow-950";
                      // } else {
                      //   return "";
                      // }
                    })()}`}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Status Dot Indicator */}
                    <div className='absolute top-2 right-2 z-10'>
                      <div
                        className={`w-3 h-3 rounded-full border-2 border-white shadow-lg ${(() => {
                          const laneStatus =
                            typeof lane.status === "string"
                              ? lane.status
                              : lane.status?.name || lane.status?.slug;

                          if (
                            laneStatus === "completed" ||
                            laneStatus === "Completed"
                          ) {
                            return "bg-blue-500";
                          } else if (
                            laneStatus === "ongoing" ||
                            laneStatus === "Ongoing" ||
                            laneStatus === "active" ||
                            laneStatus === "live"
                          ) {
                            return "bg-green-500";
                          } else if (
                            laneStatus === "paused" ||
                            laneStatus === "Paused"
                          ) {
                            return "bg-yellow-500";
                          } else if (laneStatus === "upcoming") {
                            return "bg-blue-500";
                          } else if (
                            laneStatus === "closed" ||
                            laneStatus === "completed"
                          ) {
                            return "bg-red-500";
                          } else {
                            return "bg-gray-500";
                          }
                        })()}`}
                      />
                    </div>

                    {/* Lane Header */}
                    <div className='flex items-center justify-center gap-3 mb-2'>
                      <div className='text-center'>
                        <h5 className='text-white font-semibold text-xs sm:text-sm'>
                          {lane.lane?.name ||
                            lane?.name ||
                            `Lane ${lane.laneId || idx}`}
                        </h5>
                        {/* Lane Status */}
                        {/* {lane.status && (
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${(() => {
                              const laneStatus =
                                typeof lane.status === "string"
                                  ? lane.status
                                  : lane.status?.name || lane.status?.slug;

                              if (
                                laneStatus === "ongoing" ||
                                laneStatus === "Ongoing" ||
                                laneStatus === "active" ||
                                laneStatus === "live"
                              ) {
                                return "bg-green-500 text-white";
                              } else if (
                                laneStatus === "paused" ||
                                laneStatus === "Paused"
                              ) {
                                return "bg-yellow-500 text-black";
                              } else if (laneStatus === "upcoming") {
                                return "bg-blue-500 text-white";
                              } else if (
                                laneStatus === "closed" ||
                                laneStatus === "completed"
                              ) {
                                return "bg-red-500 text-white";
                              } else {
                                return "bg-gray-500 text-white";
                              }
                            })()}`}
                          >
                            {typeof lane.status === "string"
                              ? lane.status
                              : lane.status?.name ||
                                lane.status?.slug ||
                                "unknown"}
                          </span>
                        )} */}
                      </div>
                      <div className='flex items-center justify-center gap-1 text-gray-300'>
                        <span className='text-white font-semibold text-xs sm:text-sm'>
                          {"("}
                          {lane?.auctionLaneVehicles?.length ||
                            lane.vehicles?.length ||
                            0}
                          {/* {lane.auctionLaneVehicles.length} */}
                          {")"}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className='space-y-1.5'>
                      {/* Download button is only visible on the auctions route */}
                      {window.location.pathname === "/auctions" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const laneId = lane.id || lane.laneId || idx;
                            const laneName =
                              lane.lane?.name ||
                              lane?.name ||
                              `Lane ${lane.laneId || idx}`;
                            handleDownloadPDF(laneId, laneName);
                          }}
                          className='w-full bg-gray-500 hover:bg-gray-700 text-white border border-gray-600 hover:border-gray-500 text-xs sm:text-sm px-2 py-1 transition-all duration-200 font-nsm button-sm'
                        >
                          {translations[language]?.auctionControls
                            ?.downloadList || "Download PDF"}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const laneId = lane.id || lane.laneId || idx;
                          handleViewList(laneId);
                        }}
                        className='w-full bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 hover:border-gray-500 text-xs sm:text-sm px-2 py-1 transition-all duration-200 font-nsm button-sm'
                      >
                        {translations[language]?.viewList || "View List"}
                      </button>

                      {(() => {
                        const laneStatus =
                          typeof lane.status === "string"
                            ? lane.status
                            : lane.status?.name || lane.status?.slug;

                        const isCompleted =
                          laneStatus === "completed" ||
                          laneStatus === "Completed";

                        // Show Join Auction button only when 15 minutes or less remain until start
                        const nowTz = dayjs().tz(DUBAI_TZ);
                        const startTz = dayjs
                          .utc(auction.startDate)
                          .tz(DUBAI_TZ);
                        const msUntilStart = startTz.diff(nowTz);
                        const canShowJoin = msUntilStart <= 15 * 60 * 1000; // <= 15 minutes remaining (or already started)

                        const isDisabled = isCompleted || !canShowJoin;
                        return (
                          <button
                            onClick={
                              isDisabled
                                ? undefined
                                : (e) => {
                                    e.stopPropagation();
                                    const laneId =
                                      lane.id || lane.laneId || idx;
                                    handleJoinAuction(laneId);
                                  }
                            }
                            disabled={isDisabled}
                            className={`w-full text-xs sm:text-sm px-2 py-1 transition-all duration-200 button-sm ${
                              isDisabled
                                ? "bg-gray-500 text-gray-300 cursor-not-allowed opacity-60"
                                : "bg-[#DE542E] hover:bg-[#D14A1F] text-white hover:shadow-xl"
                            }`}
                          >
                            {isCompleted
                              ? "Completed"
                              : translations[language]?.auctionControls
                                  ?.joinAuction || "Join Auction"}
                          </button>
                        );
                      })()}
                    </div>
                  </motion.div>
                ))}
            </div>

            {/* Fallback if no lanes */}
            {(!auction.auctionLanes || auction.auctionLanes.length === 0) && (
              <div className='text-center py-8 text-gray-400'>
                <svg
                  width='48'
                  height='48'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='mx-auto mb-4'
                >
                  <path
                    d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'
                    fill='#666'
                  />
                </svg>
                <p>
                  {translations[language]?.noLanesAvailable ||
                    "No auction lanes available"}
                </p>
              </div>
            )}
          </div>

          {/* Footer Stats */}
          {/* <div className="mt-3 pt-4 border-t">
          <div className="flex justify-between items-center text-sm">
            <div className="text-gray-400">
              {translations[language]?.totalCars || "Total Cars"}:
              <span className="text-white font-semibold ml-1">
                {typeof auction.totalCars === "number" ? auction.totalCars : 0}
              </span>
            </div>
            <div className="text-gray-400">
              {translations[language]?.auctionStatus || "Status"}:
              <span
                className={`font-semibold ml-1 ${
                  isActive ? "text-green-400" : "text-red-400"
                }`}
              >
                {isActive
                  ? translations[language]?.active || "Active"
                  : translations[language]?.closed || "Closed"}
              </span>
            </div>
          </div>
        </div> */}
        </div>
      </motion.div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    // Only re-render if auction data, selection state, callback function, or index changes
    return (
      prevProps.auction?.id === nextProps.auction?.id &&
      prevProps.auction?.startDate === nextProps.auction?.startDate &&
      prevProps.auction?.auction === nextProps.auction?.auction &&
      prevProps.auction?.name === nextProps.auction?.name &&
      prevProps.auction?.totalCars === nextProps.auction?.totalCars &&
      prevProps.auction?.auctionLanes?.length ===
        nextProps.auction?.auctionLanes?.length &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.onSelect === nextProps.onSelect &&
      prevProps.index === nextProps.index
    );
  }
);

export default SingleAuction;
